import {
  Bot, Code2, Smartphone, Palette, Megaphone, Cloud, Search, Workflow,
  Users, BarChart3, Lightbulb, Handshake, Layers, ShieldCheck, LineChart,
  Headphones, GraduationCap, Briefcase, School, Rocket, HeartPulse,
  ShoppingBag, Building2, Landmark, Scale,
} from "lucide-react";

export const STATS = [
  { value: 150, suffix: "+", label: "Projects Delivered" },
  { value: 50, suffix: "+", label: "Happy Clients" },
  { value: 98, suffix: "%", label: "Client Retention Rate" },
  { value: 5, suffix: "+", label: "Years Combined Expertise" },
];

export const TRUSTED_BY = [
  "Meridian Logistics", "Brightside Health", "Corestone Finance", "Oakridge Academy",
  "NovaPay", "Vantage Group", "HelixWorks", "BlueOrbit Media",
];

export const TECH_STACK = [
  "React", "Next.js", "Node.js", "Python", "FastAPI", "TypeScript",
  "AWS", "Azure", "Google Cloud", "OpenAI API", "Claude API", "TensorFlow",
  "PostgreSQL", "MongoDB", "Kubernetes", "Docker", "Flutter", "React Native",
  "Figma", "Stripe", "Terraform", "Redis",
];

export const TECH_MARQUEE = [
  "React", "Python", "Node.js", "AWS", "TensorFlow", "Flutter",
  "Kubernetes", "OpenAI", "MongoDB", "Figma", "Next.js", "Azure",
];

export const CORE_SERVICES = [
  {
    icon: Bot,
    title: "AI & Business Automation",
    desc: "Intelligent systems that automate workflows, cut costs, and unlock new capabilities.",
    tags: ["Automation", "Workflows", "LLM Integrations", "RPA"],
    big: true,
  },
  {
    icon: Code2,
    title: "Custom Software",
    desc: "Tailored platforms engineered around your exact business logic.",
    tags: ["Platforms", "APIs", "Integrations"],
  },
  {
    icon: Smartphone,
    title: "Web & Mobile Apps",
    desc: "High-performance applications across every screen and device.",
    tags: ["React", "iOS · Android", "PWA"],
  },
  {
    icon: Palette,
    title: "UI/UX & Branding",
    desc: "Interfaces and identities people remember — and love to use.",
    tags: ["Design Systems", "Prototyping", "Identity"],
  },
  {
    icon: Megaphone,
    title: "Digital Marketing",
    desc: "Performance-driven campaigns that turn attention into revenue.",
    tags: ["Paid Media", "SEO", "CRO", "Analytics"],
    wide: true,
  },
  {
    icon: Cloud,
    title: "Cloud & Consulting",
    desc: "Scalable infrastructure and strategic technology guidance.",
    tags: ["AWS · Azure", "DevOps", "Security"],
  },
];

export const DETAILED_SERVICES = [
  {
    icon: Bot,
    title: "Artificial Intelligence & Business Automation",
    desc: "We build intelligent systems that think alongside your team — automating repetitive work, surfacing insights, and enabling decisions at machine speed without losing the human touch.",
    capabilities: ["Custom AI model integration & fine-tuning", "Intelligent chatbots & virtual assistants", "Document processing & data extraction", "Predictive analytics & forecasting", "Workflow automation with AI decisioning"],
  },
  {
    icon: Code2,
    title: "Custom Software Development",
    desc: "Off-the-shelf tools force your business into their box. We engineer software around the way you actually operate — scalable, secure, and built to evolve with you.",
    capabilities: ["Enterprise web platforms & portals", "API design & third-party integrations", "Legacy system modernization", "Microservices architecture", "Ongoing maintenance & support"],
  },
  {
    icon: Smartphone,
    title: "Web & Mobile Application Development",
    desc: "From progressive web apps to native mobile experiences, we ship products that feel fast, look sharp, and hold up under real-world scale.",
    capabilities: ["React, Next.js & modern web stacks", "iOS & Android native and cross-platform", "Progressive Web Apps (PWA)", "E-commerce & marketplace builds", "Performance optimization & accessibility"],
  },
  {
    icon: Palette,
    title: "UI/UX Design & Brand Identity",
    desc: "Design is how your product speaks before a single word is read. We craft interfaces and brand systems that build instant trust and lasting recognition.",
    capabilities: ["User research & journey mapping", "Wireframing & interactive prototyping", "Design systems & component libraries", "Logo, identity & brand guidelines", "Usability testing & iteration"],
  },
  {
    icon: Megaphone,
    title: "Digital & Performance Marketing",
    desc: "We treat marketing like engineering — measured, iterated, and optimized. Every campaign is accountable to real business outcomes, not vanity metrics.",
    capabilities: ["Paid media strategy (Google, Meta, LinkedIn)", "Conversion rate optimization", "Content strategy & production", "Email & lifecycle marketing", "Attribution & campaign analytics"],
  },
  {
    icon: Search,
    title: "Search Engine Optimization (SEO)",
    desc: "Sustainable organic growth built on technical excellence and genuinely useful content — the kind search engines and humans both reward.",
    capabilities: ["Technical SEO audits & fixes", "Keyword strategy & content planning", "On-page & structural optimization", "Local SEO & Google Business profiles", "Rank tracking & reporting"],
  },
  {
    icon: Cloud,
    title: "Cloud Solutions & Technology Consulting",
    desc: "Whether you're migrating, scaling, or starting fresh, we architect cloud infrastructure that's cost-efficient, resilient, and ready for what's next.",
    capabilities: ["AWS, Azure & GCP architecture", "Cloud migration & cost optimization", "DevOps & CI/CD pipelines", "Security & compliance hardening", "Fractional CTO & technology roadmaps"],
  },
  {
    icon: Workflow,
    title: "Business Process Automation",
    desc: "We map your operations end-to-end, find the friction, and eliminate it — freeing your team to focus on work that actually needs a human.",
    capabilities: ["Process discovery & mapping", "RPA & workflow orchestration", "Approval & document automation", "System-to-system data syncing", "ROI measurement & continuous tuning"],
  },
  {
    icon: Users,
    title: "CRM & Workflow Integration",
    desc: "Your customer data should work as hard as your sales team. We implement and integrate CRM systems that give every department a single source of truth.",
    capabilities: ["CRM selection, setup & customization", "Sales pipeline & lead automation", "Marketing & support tool integration", "Custom dashboards & reporting", "Team training & adoption programs"],
  },
  {
    icon: BarChart3,
    title: "Analytics & Growth Strategy",
    desc: "Data without direction is noise. We build measurement frameworks that tell you exactly what's working, what isn't, and where to invest next.",
    capabilities: ["Analytics implementation & tracking plans", "KPI frameworks & executive dashboards", "Growth experimentation programs", "Customer behavior analysis", "Quarterly strategy & insight reviews"],
  },
];

export const PRODUCTS = [
  {
    id: "lms",
    path: "/products/lms",
    icon: GraduationCap,
    name: "The Aurge Labs - LMS",
    shortName: "LMS",
    tag: "Learning Management System",
    tagline: "Learning management, built for real engagement",
    desc: "The Aurge Labs - LMS is our comprehensive learning platform for academies, enterprises, and modern educators — engineered to make curriculum delivery seamless, interactive, and measurable.",
    tags: ["Courses", "Certificates", "Live Classes", "Analytics"],
    features: ["Course creation & curriculum builder", "Student progress tracking", "Certifications & badging", "Live classes & virtual classrooms", "Analytics dashboards", "Parent/guardian access portal"],
    accent: "#09B2ED",
  },
  {
    id: "crm",
    path: "/products/crm",
    icon: Briefcase,
    name: "The Aurge Labs - CRM",
    shortName: "CRM",
    tag: "Client & Operations Management CRM",
    tagline: "Every deal, project, and client interaction in one unified view",
    desc: "The Aurge Labs - CRM consolidates your entire client lifecycle — from lead capture and pipeline tracking to automated invoicing, contracts, and revenue forecasting.",
    tags: ["Pipeline", "Invoicing", "Tasks", "Reports"],
    features: ["Client pipeline management", "Project & task tracking", "Invoicing & payments", "Team collaboration tools", "Workflow automation", "Reporting dashboards"],
    accent: "#2563EB",
  },
  {
    id: "school",
    path: "/products/school-management-system",
    icon: School,
    name: "The Aurge Labs - School Management System",
    shortName: "School Management System",
    tag: "Complete School Administration & SIS",
    tagline: "Your entire campus operations, connected in real time",
    desc: "The Aurge Labs - School Management System is the all-in-one digital operating system for K-12 schools, colleges, and educational institutions — streamlining admissions, fee collection, attendance, grading, and parent portals.",
    tags: ["Admissions", "Attendance", "Fees", "Grading"],
    features: ["Admissions & enrollment management", "Real-time attendance tracking", "Automated fee & invoicing engine", "Timetable & scheduling matrix", "Parent-teacher portal", "Exam & grading modules"],
    accent: "#91E0F1",
  },
];

export const DIFFERENTIATORS = [
  { icon: Lightbulb, title: "Innovation-First Approach", desc: "We reach for what's next, not what's safe — every build pushes boundaries." },
  { icon: Handshake, title: "Long-Term Partnerships", desc: "We measure success in years of partnership, not billable hours." },
  { icon: Layers, title: "Multidisciplinary Expertise", desc: "Engineers, designers, and strategists working as one integrated team." },
  { icon: ShieldCheck, title: "Scalable & Secure Builds", desc: "Architecture that grows with you, hardened against what could break it." },
  { icon: LineChart, title: "Data-Driven Results", desc: "Every decision backed by evidence, every outcome measured." },
  { icon: Headphones, title: "Dedicated Support", desc: "Real humans, real response times — long after launch day." },
];

export const PROCESS_STEPS = [
  { num: "01", title: "Discover", desc: "We dig deep into your goals, users, and constraints before writing a line of code." },
  { num: "02", title: "Design", desc: "Prototypes and systems that make the solution tangible — fast." },
  { num: "03", title: "Develop", desc: "Agile sprints with weekly demos, so you're never in the dark." },
  { num: "04", title: "Deploy", desc: "Rigorous QA, smooth launches, and zero-drama go-lives." },
  { num: "05", title: "Optimize", desc: "Post-launch analytics, iteration, and growth — the work never really ends." },
];

export const TESTIMONIALS = [
  {
    quote: "The Aurge Labs rebuilt our entire operations platform in four months and migrated 11 years of legacy data without a single day of downtime. Invoice processing that used to take our team 3 days now completes in under 40 minutes. They're not vendors — they're partners.",
    name: "Sarah Mitchell",
    role: "Chief Operating Officer",
    company: "Meridian Logistics",
    rating: 5,
  },
  {
    quote: "We came to them for a website and left with a growth engine. Organic traffic grew 212% in six months, our cost-per-lead dropped 38%, and their team still runs a weekly optimization call with us. Best agency decision we've made.",
    name: "David Okafor",
    role: "Founder & CEO",
    company: "Brightside Health",
    rating: 5,
  },
  {
    quote: "Their AI document pipeline cut our loan-file processing time by 90% while improving accuracy to 99.4%. The ROI paid for the entire engagement within the first quarter — our board asked us to expand the contract.",
    name: "Priya Raghavan",
    role: "VP of Operations",
    company: "Corestone Finance",
    rating: 5,
  },
  {
    quote: "The School Management System from Aurge transformed how our school runs. Teachers save roughly 12 hours a week on admin, fee collection is up to 96% on-time, and parents finally have real visibility. Rollout took three weeks, exactly as promised.",
    name: "James Whitfield",
    role: "Principal",
    company: "Oakridge Academy",
    rating: 5,
  },
];

export const TEAM = [
  { name: "Alex Reyes", role: "Founder & CEO", initials: "AR" },
  { name: "Maya Chen", role: "Head of Design", initials: "MC" },
  { name: "Tomás Silva", role: "Lead Engineer", initials: "TS" },
  { name: "Nadia Hassan", role: "AI & Data Lead", initials: "NH" },
  { name: "Ethan Brooks", role: "Growth Strategist", initials: "EB" },
  { name: "Lina Park", role: "Product Manager", initials: "LP" },
];

export const VALUES = [
  { title: "Innovation", desc: "We chase better, not just newer." },
  { title: "Integrity", desc: "Honest estimates, transparent work, no surprises." },
  { title: "Partnership", desc: "Your goals become our roadmap." },
  { title: "Excellence", desc: "Good enough never is." },
  { title: "Impact", desc: "Technology that moves real numbers." },
];

export const INDUSTRIES = [
  { icon: Rocket, label: "Startups & SaaS" },
  { icon: GraduationCap, label: "Education" },
  { icon: HeartPulse, label: "Healthcare" },
  { icon: ShoppingBag, label: "E-commerce & Retail" },
  { icon: Landmark, label: "Real Estate" },
  { icon: Scale, label: "Professional Services" },
  { icon: Building2, label: "Enterprise & Corporate" },
];

export const MILESTONES = [
  { year: "2021", title: "The lab opens", desc: "Founded by engineers with one conviction: technology should empower." },
  { year: "2022", title: "First enterprise client", desc: "Shipped our first large-scale automation platform." },
  { year: "2023", title: "EduCults launches", desc: "Our first proprietary SaaS product goes live." },
  { year: "2024", title: "AgencyOrbits ships", desc: "Second product launches; 100th project delivered." },
  { year: "2025", title: "School Management System", desc: "Third product completes the suite; 50+ active clients." },
  { year: "2026", title: "150+ projects & counting", desc: "A full-stack transformation partner across 12 industries." },
];

export const FAQS = [
  { q: "How long does a typical project take?", a: "Most projects run 6–16 weeks depending on scope. A marketing website typically ships in 4–6 weeks, while custom software platforms run 3–4 months. We give you a detailed timeline with milestones before any work begins." },
  { q: "How do you approach pricing?", a: "We scope every project individually and offer both fixed-price and retainer models. After a free consultation, you receive a transparent proposal with clear deliverables — no hidden costs, no hourly billing surprises." },
  { q: "What engagement models do you offer?", a: "Three ways to work with us: project-based (defined scope, fixed timeline), dedicated team (embedded engineers and designers), or ongoing retainer (continuous development, support, and optimization)." },
  { q: "Do you provide support after launch?", a: "Always. Every project includes a post-launch support window, and most clients continue with a maintenance retainer covering updates, monitoring, and iterative improvements." },
  { q: "Can you work with our existing systems?", a: "Yes — integration is one of our core strengths. We regularly connect with existing CRMs, ERPs, payment systems, and legacy databases rather than forcing costly replacements." },
  { q: "What happens after I submit the contact form?", a: "You'll hear from a senior team member (never a bot) within one business day. We'll schedule a free 30-minute call, understand your goals, and follow up with a written summary and recommended next steps — no obligation." },
  { q: "Do you sign NDAs?", a: "Absolutely. We're happy to sign your NDA before any detailed discussion, or provide our standard mutual NDA. Confidentiality is table stakes for the industries we serve." },
  { q: "Can I get a demo of EduCults, AgencyOrbits, or the School Management System?", a: "Yes — select 'Product Demo' in the contact form and we'll set up a personalized walkthrough on your own use case, usually within 2–3 business days." },
];

export const ENGAGEMENT_MODELS = [
  {
    title: "Fixed Scope",
    desc: "A defined project with a locked budget, timeline, and deliverables. Best for well-specified builds.",
    features: ["Detailed proposal & milestones upfront", "Fixed price — no billing surprises", "Weekly demo & progress reports", "30-day post-launch warranty"],
    cta: "Get an Estimate",
  },
  {
    title: "Dedicated Team",
    desc: "A cross-functional Aurge squad — engineers, designer, PM — embedded in your business long-term.",
    features: ["Hand-picked team for your stack", "Direct daily communication", "Sprint-based agile delivery", "Scale team size up or down monthly"],
    cta: "Build Your Team",
    featured: true,
  },
  {
    title: "Staff Augmentation",
    desc: "Senior specialists who slot into your existing team and processes from day one.",
    features: ["Vetted senior-level talent only", "Start within 1–2 weeks", "Your tools, your workflow", "Simple monthly engagement"],
    cta: "Add Specialists",
  },
];

export const TRUST_BADGES = [
  "NDA on Request",
  "GDPR-Ready",
  "24-Hour Response",
  "ISO-Aligned Practices",
];

export const MEGA_MENU = {
  services: {
    to: "/services",
    width: "w-[min(880px,92vw)]",
    columns: [
      {
        heading: "Build",
        items: [
          { label: "AI & Business Automation", desc: "Intelligent systems & workflows" },
          { label: "Custom Software Development", desc: "Platforms built around your logic" },
          { label: "Web & Mobile Applications", desc: "High-performance digital products" },
          { label: "Cloud Solutions & Consulting", desc: "Scalable, secure infrastructure" },
        ],
      },
      {
        heading: "Design",
        items: [
          { label: "UI/UX & Brand Identity", desc: "Interfaces people love to use" },
          { label: "Business Process Automation", desc: "Eliminate operational friction" },
          { label: "CRM & Workflow Integration", desc: "One source of truth for teams" },
        ],
      },
      {
        heading: "Grow",
        items: [
          { label: "Digital & Performance Marketing", desc: "Campaigns accountable to revenue" },
          { label: "SEO", desc: "Sustainable organic growth" },
          { label: "Analytics & Growth Strategy", desc: "Measure what actually matters" },
        ],
      },
    ],
    promo: {
      title: "Not sure where to start?",
      desc: "Book a free 30-min consultation with a senior expert.",
      cta: "Book Now",
    },
  },
  products: {
    to: "/products",
    width: "w-[min(560px,92vw)]",
    columns: [
      {
        heading: "Our SaaS Products",
        items: [
          { label: "The Aurge Labs - LMS", to: "/products/lms", desc: "Learning management, built for engagement" },
          { label: "The Aurge Labs - CRM", to: "/products/crm", desc: "Every client & project, one clear view" },
          { label: "The Aurge Labs - School Management System", to: "/products/school-management-system", desc: "Your entire school, connected in real-time" },
          { label: "Explore All Products", to: "/products", desc: "Compare features, use cases & architecture" },
        ],
      },
    ],
    promo: {
      title: "See Our Products Live",
      desc: "A personalized walkthrough on your own data.",
      cta: "Request Demo",
    },
  },
};

export const PORTFOLIO = [
  {
    title: "Meridian Logistics Platform",
    category: "Custom Software · AI Automation",
    result: "90% faster document processing",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=75",
  },
  {
    title: "Brightside Health Growth Engine",
    category: "Web Development · Digital Marketing",
    result: "3× organic traffic in 6 months",
    image: "https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?auto=format&fit=crop&w=900&q=75",
  },
  {
    title: "Oakridge Academy Digital Campus",
    category: "School Management System",
    result: "12 hrs/week saved per teacher",
    image: "https://images.pexels.com/photos/8068807/pexels-photo-8068807.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    title: "Corestone Finance Automation",
    category: "AI & Business Automation",
    result: "ROI recovered in one quarter",
    image: "https://images.pexels.com/photos/7698712/pexels-photo-7698712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];
